import { ColDef } from 'ag-grid-community';

import Typography from '~/shared/components/Typography';
import Exception from '~/shared/assets/icons/exception-2.svg';
import { Account } from '~/shared/utils/types';
import SizeCell from '~/shared/components/SizeCell';
import { SkeletonCellRenderer } from '~/shared/components/Table/SkeletonCellRenderer';
import Tooltip from '~/shared/components/Tooltip';

import { AutoscaleRule } from './hooks/useAutoscaleRulesQuery';
import ConditionsRenderer from './ConditionsRenderer';
import ToggleRuleRenderer from './ToggleRuleRenderer';

type AutoscaleRuleWithSkeleton = AutoscaleRule & { _isSkeleton?: boolean };

export function getAutoscaleRulesColumnDefs(
  accountName?: string,
  isOrganizationAccount?: boolean,
  windowWidth?: number,
  isOrgView?: boolean,
  accounts?: Account[],
  canWrite = false
): ColDef<AutoscaleRuleWithSkeleton>[] {
  const isSmallScreen = (windowWidth || 1600) <= 1440;


  const numColWidth = isOrgView ? 90 : 100;
  const metaColWidth = isOrgView ? 105 : 120;

  const columns: ColDef[] = [
    {
      field: 'ruleId',
      headerName: 'Rule ID',
      width: 200,
      minWidth: 200,
      suppressSizeToFit: true,
      flex: 1,
      sortable: false,
      cellRenderer: ({ value, data }: { value: string; data: AutoscaleRuleWithSkeleton }) => {
        if (data?._isSkeleton) {
          return <SkeletonCellRenderer width={150} />;
        }
        return (
          <Typography
            title={value}
            variant="p3-reg"
            color="text-blue-600"
            className="font-medium text-[14px] overflow-hidden whitespace-nowrap text-ellipsis"
          >
            {value || '-'}
          </Typography>
        );
      },
    },
    {
      field: 'conditions',
      headerName: 'Conditions',
      width: isOrgView ? (isSmallScreen ? 340 : 560) : (isSmallScreen ? 488 : 868),
      flex: 3,
      minWidth: isOrgView ? 300 : 488,
      resizable: true,
      sortable: false,
      cellRenderer: ({ data }: { data: AutoscaleRuleWithSkeleton }) => {
        if (data?._isSkeleton) {
          return <SkeletonCellRenderer width={400} />;
        }
        return <ConditionsRenderer data={data} />;
      },
      valueFormatter: () => '', // Suppress AG Grid warning for object data type
    },
    {
      field: 'volumes',
      headerName: 'Volumes',
      width: numColWidth,
      suppressSizeToFit: true,
      sortable: false,
      cellRenderer: ({ value, data }: { value: number; data: AutoscaleRuleWithSkeleton }) => {
        if (data?._isSkeleton) {
          return <SkeletonCellRenderer width={60} />;
        }
        return (
          <Typography
            variant="p3-reg"
            color="text-gray-900"
            fontWeight={500}
            className="text-[14px]"
          >
            {value?.toLocaleString() || '-'}
          </Typography>
        );
      },
    },
    {
      field: 'capacity',
      headerName: 'Capacity',
      width: metaColWidth,
      suppressSizeToFit: true,
      sortable: false,
      cellRenderer: ({ value, data }: { value: number; data: AutoscaleRuleWithSkeleton }) => {
        if (data?._isSkeleton) {
          return <SkeletonCellRenderer width={80} />;
        }
        const size = value || 0;

        return size !== undefined && size !== null ? <SizeCell size={size} /> : 'N/A';
      },
    },
    {
      field: 'utilization',
      headerName: 'Utilization',
      width: metaColWidth,
      suppressSizeToFit: true,
      sortable: false,
      cellRenderer: ({ value, data }: { value: string; data: AutoscaleRuleWithSkeleton }) => {
        if (data?._isSkeleton) {
          return <SkeletonCellRenderer width={60} />;
        }
        const showNA = !value || data.volumes === 0;
        const displayValue = showNA ? 'N/A' : value;

        return (
          <div className="flex items-center">
            <Typography variant="p3-reg" color="text-gray-900" className="min-w-[3rem] text-[14px]">
              {displayValue}
            </Typography>
          </div>
        );
      },
    },
    {
      field: 'exceptions',
      headerName: 'Exceptions',
      width: metaColWidth,
      suppressSizeToFit: true,
      sortable: false,
      cellRenderer: ({ value, data }: { value: number; data: AutoscaleRuleWithSkeleton }) => {
        if (data?._isSkeleton) {
          return (
            <div className="flex items-center gap-1.5">
              <SkeletonCellRenderer width={20} height={20} />
              <SkeletonCellRenderer width={20} />
            </div>
          );
        }
        return (
          <div className="flex items-center gap-1.5">
            {value === 0 ? (
              <div className="size-5 rounded-full bg-transparent" />
            ) : (
              <Exception className="size-5 text-gray-600" />
            )}
            <Typography
              variant="p3-reg"
              fontWeight={500}
              color="text-gray-900"
              className="text-[14px]"
            >
              {value}
            </Typography>
          </div>
        );
      },
    },
  ];

  if (isOrgView && accounts) {
    columns.push({
      field: 'accountId',
      headerName: 'Account',
      flex: 1,
      minWidth: 200,
      maxWidth: 250,
      sortable: true,
      cellRenderer: ({ value, data }: { value: string; data: AutoscaleRuleWithSkeleton }) => {
        if (data?._isSkeleton) {
          return <SkeletonCellRenderer width={80} />;
        }
        const account = accounts.find((acc) => acc.accountId === value);
        const displayName = account?.accountName || value || '-';
        const tooltipId =
          displayName !== '-' ? `autoscale-rules-account-${data?.ruleId || value || 'unknown'}` : undefined;

        return (
          <>
            <div className="flex justify-start overflow-hidden" data-tooltip-id={tooltipId}>
              <Typography
                variant="p3-reg"
                fontWeight={500}
                color="text-[#071031]"
                className="text-[14px] truncate"
              >
                {displayName}
              </Typography>
            </div>
            {tooltipId && <Tooltip id={tooltipId} content={displayName} place="top" />}
          </>
        );
      },
    });
  }

  // Add Toggle column at the end
  columns.push({
    field: 'toggle' as any,
    headerName: '',
    width: 104,
    suppressSizeToFit: true,
    suppressNavigable: true,
    cellRenderer: ({ data }: { data: AutoscaleRuleWithSkeleton }) => {
      if (data?._isSkeleton) {
        return <SkeletonCellRenderer width={60} variant="toggle" />;
      }
      return <ToggleRuleRenderer data={data} canWrite={canWrite} isOrganizationAccount={isOrganizationAccount} />;
    },
  });

  return columns;
}
